import type { MouseEvent } from "react";
import { getSlug } from "@huub-web/utils";
import { Flex, Icon, Text } from "@huub-web/ui";

import type { HotspotRecord } from "../../MapboxMapView";
import type { Amenity } from "../../../types";
import { formatDuration } from "../../MapControlCard/utils/formatters";

export type GetAmenityHotspotParams = {
  /**
   * Unique identifier for the clustered hotspots
   */
  sessionID: string;
  /**
   * Address of the property (used for unique ID generation)
   */
  address: string;
  /**
   * Amenity data with required center coordinates
   */
  amenity: Amenity & { center: [number, number] };
  /**
   * Time to get to the amenity in minutes
   */
  time?: number;
  /**
   * Index for staggered animation delay
   */
  index: number;
  /**
   * Whether this amenity hotspot is active (has route displayed)
   */
  active?: boolean;
  /**
   * Click handler for the amenity hotspot
   */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /**
   * Callback when close button is clicked (cancels the route)
   */
  onClose?: () => void;
};

/**
 * Creates a hotspot record for an amenity point of interest
 * @param params - Configuration parameters for the amenity hotspot
 * @returns HotspotRecord for the amenity marker
 */
export const getAmenityHotspot = ({
  sessionID,
  address,
  amenity,
  time,
  active,
  onClick,
  onClose,
}: GetAmenityHotspotParams): HotspotRecord => ({
  id: getSlug(
    `${sessionID} ${address} ${amenity.name} ${amenity.center[0]} ${amenity.center[1]}`,
  ),
  type: "poi",
  lat: amenity.center[0],
  lng: amenity.center[1],
  icon: amenity.icon,
  category: amenity.category,
  // When active, use card-style tooltip with title/subtitle
  ...(active
    ? {
        title: amenity.name,
        subtitle: (
          <Flex direction="column" gap="xxs">
            {amenity.type && (
              <Text variant="surfaceSubtitle" color="currentColor50">
                {amenity.type}
              </Text>
            )}
            <Flex align="center" gap="xs">
              {time && (
                <Flex gap="xxs" align="center">
                  <Icon icon="walking" size="s" color="currentColor50" />
                  <Text variant="bodyS" color="currentColor50" grow>
                    {formatDuration(time)}
                  </Text>
                </Flex>
              )}
              {amenity.distance && (
                <Flex gap="xxs" align="center">
                  <Icon icon="pin" size="s" color="currentColor50" />
                  <Text variant="bodyS" color="currentColor50">
                    {amenity.distance}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        ),
        closable: true,
        onClose,
      }
    : {
        tooltip: (
          <Flex direction="column" gap="xxs2">
            <Text variant="bodyS" bold>
              {amenity.name ?? amenity.type}
            </Text>
            <Text variant="bodyS" color="currentColor75">
              {amenity.distance} • {amenity.type}
            </Text>
          </Flex>
        ),
      }),
  onClick,
  active,
  noClustering: active,
});
